import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Loader2, Upload, Smartphone, ShieldCheck, Trash2, QrCode, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { money, useSite, fileToDataUrl } from "@/lib/site";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Brand";
import { MentorAvatar } from "@/components/MentorCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const errDetail = (e, f) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : f; };

const UpiBox = ({ site, amount, note }) => {
  const link = `upi://pay?pa=${encodeURIComponent(site.upi_id)}&pn=${encodeURIComponent(site.upi_name || "Motley Mentors")}&am=${amount}&cu=${site.currency || "INR"}&tn=${encodeURIComponent(note)}`;
  const copy = () => { navigator.clipboard.writeText(site.upi_id); toast.success("UPI ID copied"); };
  return (
    <div className="rounded-3xl bg-[#0B2F55] text-[#F5F1E8] p-6" data-testid="upi-box">
      <p className="font-body text-xs uppercase tracking-[0.2em] text-sky-300">Step 2 · Pay via UPI</p>
      <p className="font-heading font-extrabold text-4xl mt-2" data-testid="upi-amount">{money(amount, site.currency)}</p>
      <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-5 items-center">
        <div>
          <p className="font-body text-xs text-[#F5F1E8]/60">Pay to UPI ID</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono font-bold text-lg break-all" data-testid="upi-id">{site.upi_id}</span>
            <button onClick={copy} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="copy-upi-btn"><Copy className="w-4 h-4" /></button>
          </div>
          <p className="font-body text-xs text-[#F5F1E8]/60 mt-1">{site.upi_name}</p>
          <a href={link} className="sm:hidden mt-4 inline-flex items-center gap-2 rounded-full bg-[#F5F1E8] text-[#0B2F55] px-5 py-2.5 font-heading font-bold text-sm" data-testid="upi-pay-link">
            <Smartphone className="w-4 h-4" /> Open UPI app
          </a>
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <img src={site.upi_qr || `${process.env.REACT_APP_BACKEND_URL}/api/site/upi-qr?amount=${amount}&note=${encodeURIComponent(note)}`} alt="Scan to pay via UPI"
            className="w-40 h-40 rounded-2xl bg-white p-2 object-contain" data-testid="upi-qr" />
          <p className="font-body text-[11px] text-[#F5F1E8]/60 inline-flex items-center gap-1"><QrCode className="w-3 h-3" /> Scan with any UPI app{site.upi_qr ? "" : ` · ${money(amount, site.currency)} pre-filled`}</p>
        </div>
      </div>
      <p className="font-body text-xs text-[#F5F1E8]/60 mt-5">Add <span className="font-mono">{note}</span> as the payment note if your app allows it.</p>
      {site.whatsapp && (
        <a href={`https://wa.me/91${site.whatsapp.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi Motley Mentors, I've paid ${money(amount, site.currency)} via UPI for ${note}. Sharing the payment screenshot.`)}`} target="_blank" rel="noreferrer"
          className="mt-4 flex items-center gap-3 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 px-4 py-3 hover:bg-emerald-500/25 transition-colors" data-testid="upi-whatsapp-link">
          <MessageCircle className="w-5 h-5 text-emerald-300 shrink-0" />
          <p className="font-body text-sm text-[#F5F1E8]">Send a screenshot of the payment to WhatsApp <span className="font-heading font-bold">{site.whatsapp}</span> for faster confirmation.</p>
        </a>
      )}
    </div>
  );
};

const Checkout = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const site = useSite();
  const type = params.get("type") === "assessment" ? "assessment" : "mentor";
  const plan = params.get("plan") === "monthly" ? "monthly" : "session";
  const track = params.get("track") === "start" ? "start" : "comeback";
  const [mentor, setMentor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", utr: "" });
  const [proof, setProof] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (type === "mentor") api.get(`/mentors/${params.get("mentor")}`).then((r) => setMentor(r.data)).catch(() => toast.error("Mentor not found"));
  }, [type, params]);

  const amount = useMemo(() => {
    if (type === "assessment") return site.assessment_price || 0;
    if (!mentor) return 0;
    return plan === "monthly" ? mentor.monthly_price : mentor.session_price;
  }, [type, plan, mentor, site]);
  const item = type === "assessment"
    ? `CareerCompass ${track === "start" ? "Start" : "Comeback"} assessment`
    : mentor ? `${plan === "monthly" ? "Monthly mentorship" : `${mentor.session_minutes || 45}-min session`} with ${mentor.name}` : "…";
  const note = type === "assessment" ? `MM ${track}` : `MM ${(mentor?.name || "").split(" ")[0]}`;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { setProof(await fileToDataUrl(f)); } catch (err) { toast.error(err.message); }
    e.target.value = "";
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Your name and email are required."); return; }
    if (!form.utr.trim() && !proof) { toast.error("Add the UPI transaction ID or upload a payment screenshot."); return; }
    setBusy(true);
    try {
      const r = await api.post("/orders", { type, mentor_id: mentor?.mentor_id, plan, track, ...form, proof, base_url: window.location.origin });
      toast.success("Request submitted! We'll confirm shortly.");
      navigate(`/order/${r.data.order_ref}`, { state: r.data });
    } catch (err) { toast.error(errDetail(err, "Could not submit. Please try again.")); }
    finally { setBusy(false); }
  };

  const ready = site.payments_ready;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900" data-testid="checkout-title">Complete your request</h1>
        <p className="font-body text-slate-500 mt-2">Three quick steps: your details, UPI payment, then share the transaction ID. We confirm manually and get you going.</p>

        {site.upi_id === undefined ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : !ready ? (
          <div className="mt-10 rounded-3xl bg-amber-50 border border-amber-200 p-8 font-body text-amber-900" data-testid="payments-not-ready">
            <p className="font-heading font-bold text-lg">Payments are being set up</p>
            <p className="text-sm mt-2">We're not accepting online payments just yet. {site.contact_email ? <>Write to <a className="underline" href={`mailto:${site.contact_email}`}>{site.contact_email}</a> and we'll help you directly.</> : "Please check back soon."}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="grid lg:grid-cols-[1fr_0.9fr] gap-8 mt-8">
            <div className="space-y-6">
              <section className="bg-white rounded-3xl border border-slate-200 p-6" data-testid="checkout-summary">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">You're getting</p>
                <div className="flex items-center gap-4 mt-3">
                  {mentor && <MentorAvatar mentor={mentor} className="w-14 h-14" />}
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-lg text-slate-900" data-testid="checkout-item">{item}</p>
                    <p className="font-body text-sm text-slate-500">{type === "assessment" ? "Access code + one-time password emailed after confirmation · AI-written report" : plan === "monthly" ? "Billed once for the coming month · we introduce you by email" : "Introduced by email · mentor schedules within 48h"}</p>
                  </div>
                  <p className="ml-auto font-heading font-extrabold text-2xl text-slate-900 shrink-0" data-testid="checkout-amount">{money(amount, site.currency)}</p>
                </div>
              </section>

              <section className="bg-white rounded-3xl border border-slate-200 p-6 space-y-4" data-testid="checkout-details">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">Step 1 · Your details</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Full name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-xl" data-testid="checkout-name" required /></div>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-xl" data-testid="checkout-email" required /></div>
                </div>
                <div><Label>Phone / WhatsApp <span className="text-slate-400 font-normal">(recommended — we confirm faster on WhatsApp)</span></Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5 h-11 rounded-xl" data-testid="checkout-phone" /></div>
                <div>
                  <Label>{type === "mentor" ? "What would you like help with?" : "Anything we should know?"} <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Textarea value={form.message} onChange={set("message")} rows={3} className="mt-1.5 rounded-xl" data-testid="checkout-message" />
                </div>
              </section>
            </div>

            <div className="space-y-6">
              <UpiBox site={site} amount={amount} note={note} />
              <section className="bg-white rounded-3xl border border-slate-200 p-6 space-y-4" data-testid="checkout-proof">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">Step 3 · Confirm payment</p>
                <div><Label>UPI transaction ID (UTR)</Label><Input value={form.utr} onChange={set("utr")} placeholder="e.g. 4139XXXXXXXX" className="mt-1.5 h-11 rounded-xl font-mono" data-testid="checkout-utr" /></div>
                <div>
                  <Label>Or upload payment screenshot</Label>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="checkout-proof-input" />
                  {proof ? (
                    <div className="mt-2 flex items-center gap-3">
                      <img src={proof} alt="Payment proof" className="h-20 rounded-xl border border-slate-200 object-cover" data-testid="checkout-proof-preview" />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setProof("")} data-testid="checkout-proof-remove"><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="mt-2 rounded-xl" data-testid="checkout-proof-btn"><Upload className="w-4 h-4 mr-1.5" /> Choose image</Button>
                  )}
                </div>
                <Button type="submit" disabled={busy || !amount} className="w-full rounded-full h-12 font-heading font-bold text-base" data-testid="checkout-submit">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} I've paid — submit request
                </Button>
                <p className="font-body text-xs text-slate-400 text-center">By continuing you agree to be contacted by Motley Mentors about this request. Questions? <Link to="/contact" className="underline">Get in touch</Link>.</p>
              </section>
            </div>
          </form>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default Checkout;
